import { ChevronDown } from "baseui/icon";
import React, {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import { showStatus } from "../../../constants/constants";
import {
  toUpdateWorkTaskModel,
  WorkTaskModel,
} from "../../../factories/work/model/work-task.model";
import { useUpdateStatusWorkMutation } from "../../../slices/WorkSlice";
import { useUpdateWorkTaskMutation } from "../../../slices/WorkTasksSlice";
import { CurrentStatus } from "../board/worksBoard.utils";
import { StatusesList } from "./StatusesList";
import { StatusMenuList } from "./StatusMenuList";
import { getStatusColor, menuLookTop } from "./utils";

type Props = {
  workTypeId: string;
  workId: string;
  triggerElement: ReactNode;
  triggerClassName?: string;
  positionType?: "right" | "right-bottom" | "bottom" | "right-top";
  workTask?: WorkTaskModel;
  /**
   * Work page "Select status" look (Roadmap 1858) instead of the old list:
   * opens below the trigger (above it when there's no room), marks `current`.
   */
  menuLook?: boolean;
  current?: CurrentStatus;
  /** Trigger to show after a change (menuLook), instead of the old grey one. */
  renderUpdatedTrigger?: (work: {
    jobStatus?: string | null;
    jobSubStatus?: string | null;
  }) => ReactNode;
};

export const StatusesDropdown: FC<Props> = ({
  workTypeId,
  workId,
  triggerElement,
  triggerClassName = "w-fit",
  positionType = "right",
  workTask,
  menuLook,
  current,
  renderUpdatedTrigger,
}) => {
  const [updateStatusWork, { isLoading: isLoadingUpdate, data: updatedWork }] =
    useUpdateStatusWorkMutation();
  const [
    updateContactNote,
    { isLoading: isLoadingWorkTask, data: updatedWorkTask },
  ] = useUpdateWorkTaskMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [isHide, setIsHide] = useState(false);
  const [position, setPosition] = useState<CSSProperties | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [picked, setPicked] = useState<{
    statusId: string | null;
    subStatusId: string | null;
  } | null>(null);

  const handleUpdateStatusWork = ({
    statusId,
    subStatusId,
  }: {
    statusId: string | null;
    subStatusId: string | null;
  }) => {
    setPicked({ statusId, subStatusId });
    if (workTask) {
      updateContactNote(
        toUpdateWorkTaskModel(workTask, {
          statusId: statusId,
          subStatusId: subStatusId,
        }),
      );
    } else {
      updateStatusWork({
        workId: workId,
        statusId: statusId,
        subStatusId: subStatusId,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target;

      if (
        menuRef.current &&
        target instanceof Node &&
        !triggerRef.current?.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight;
      if (menuLook) {
        setPosition({
          top: menuLookTop(triggerRect, menuHeight, window.innerHeight),
          left: triggerRect.left,
        });
        setIsHide(false);
        return;
      }
      if (positionType === "bottom") {
        setPosition({
          top: triggerRect.bottom + 4,
          left: triggerRect?.left,
        });
        setIsHide(false);
        return;
      }
      if (positionType === "right-bottom") {
        setPosition({
          top: triggerRect.top,
          left: triggerRect?.right + 4,
        });
        setIsHide(false);
        return;
      } else {
        setPosition({
          top: triggerRect.bottom - menuHeight,
          left: triggerRect?.right + 4,
        });
      }
      setIsHide(false);
    }

    if (!isOpen) {
      setIsHide(true);
    }
  }, [isOpen, triggerRef, menuRef, positionType, menuLook]);

  // The status list loads after the menu opens, so its first height is small
  // and it "fit" below even on the last rows; when it grew it ran off the
  // screen. Place it again whenever its height changes (QA batch 16,
  // HOME-020): upward when there is no room below.
  useEffect(() => {
    const menu = menuRef.current;
    if (!menuLook || !isOpen || !menu || typeof ResizeObserver === "undefined")
      return;
    const place = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      setPosition({
        top: menuLookTop(triggerRect, menu.offsetHeight, window.innerHeight),
        left: triggerRect.left,
      });
    };
    const observer = new ResizeObserver(place);
    observer.observe(menu);
    return () => observer.disconnect();
  }, [isOpen, menuLook, triggerRef, menuRef]);

  const handleTriggerClick = () => {
    setIsOpen((prev) => !prev);
  };

  const portalElement = document.getElementById("portal-root");
  if (!portalElement) {
    return null;
  }

  const renderStatusContent = () => {
    if (isLoadingUpdate || isLoadingWorkTask) {
      return (
        <div
          className={
            "flex max-w-full cursor-pointer items-center gap-1 overflow-hidden whitespace-nowrap rounded-[8px] border-[1px] border-gray-200 bg-white px-1 py-0.5 text-sm font-semibold text-gray-800"
          }>
          <div className={"text-purple h-[10px] w-[10px] animate-spin"}>
            <AiOutlineLoading3Quarters size={10} />
          </div>
          <div className={"min-w-0 truncate"}>Loading...</div>
          <ChevronDown size={16} className={"shrink-0"} />
        </div>
      );
    }
    if (updatedWorkTask) {
      return (
        <div
          className={
            "flex max-w-full cursor-pointer items-center gap-1 overflow-hidden whitespace-nowrap rounded-[8px] border-[1px] border-gray-200 bg-white px-1 py-0.5 text-sm font-semibold text-gray-800"
          }>
          <div
            className={"flex h-[10px] w-[10px] shrink-0 rounded-full"}
            style={{
              backgroundColor: getStatusColor(
                updatedWorkTask?.status?.name || "",
              ),
            }}
          />
          <div className={"min-w-0 truncate"}>
            {showStatus(null, updatedWorkTask)}
          </div>
          <ChevronDown size={16} className={"shrink-0"} />
        </div>
      );
    }
    if (updatedWork && renderUpdatedTrigger) {
      return renderUpdatedTrigger(updatedWork);
    }
    if (updatedWork) {
      return (
        <div
          className={
            "flex max-w-full cursor-pointer items-center gap-1 overflow-hidden whitespace-nowrap rounded-[8px] border-[1px] border-gray-200 bg-white px-1 py-0.5 text-sm font-semibold text-gray-800"
          }>
          <div
            className={"flex h-[10px] w-[10px] shrink-0 rounded-full"}
            style={{
              backgroundColor: getStatusColor(updatedWork?.jobStatus || ""),
            }}
          />
          <div className={"min-w-0 truncate"}>{showStatus(updatedWork)}</div>
          <ChevronDown size={16} className={"shrink-0"} />
        </div>
      );
    }
    return triggerElement;
  };

  return (
    <>
      <div
        onClick={handleTriggerClick}
        ref={triggerRef}
        className={triggerClassName}>
        {renderStatusContent()}
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              opacity: isHide ? 0 : 1,
              ...position,
              fontSize: "14px",
            }}
            ref={menuRef}>
            {menuLook ? (
              <StatusMenuList
                setIsOpen={setIsOpen}
                workTypeId={workTypeId}
                updateStatusWork={handleUpdateStatusWork}
                current={updatedWork && picked ? picked : current}
              />
            ) : (
              <StatusesList
                setIsOpen={setIsOpen}
                workTypeId={workTypeId}
                updateStatusWork={handleUpdateStatusWork}
              />
            )}
          </div>,
          portalElement,
        )}
    </>
  );
};
